/**
 * Blog archive: AJAX category/tag/search filter with URL updates.
 * Shareable URLs: direct navigation and back/forward work correctly.
 *
 * @package MorrFitHP
 */

(function () {
  const DEBOUNCE_MS = 300;
  const GRID_SEL = '#blog-archive-grid';
  const FEATURED_SEL = '#blog-featured-section';
  const PAGINATION_SEL = '#blog-archive-pagination';
  const FILTER_BTN_SEL = '.js-blog-filter';
  const SEARCH_INPUT_SEL = '#blog-search';
  const DATA_EL = document.getElementById('blog-archive-data');

  const grid = document.querySelector(GRID_SEL);
  const featuredSection = document.querySelector(FEATURED_SEL);
  const paginationContainer = document.querySelector(PAGINATION_SEL);
  const searchInput = document.querySelector(SEARCH_INPUT_SEL);

  if (!grid || !DATA_EL) return;

  let config = {};
  try {
    config = JSON.parse(DATA_EL.textContent || '{}');
  } catch (e) {
    return;
  }

  const ajaxUrl = config.ajaxUrl || '';
  const nonce = config.nonce || '';
  const baseUrl = config.baseUrl || window.location.origin + window.location.pathname;

  function debounce(fn, ms) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function getUrlParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      category: params.get('category_name') || params.get('category') || '',
      tag: params.get('tag') || '',
      search: params.get('blog_search') || params.get('search') || '',
      page: parseInt(params.get('page') || params.get('paged') || '1', 10),
    };
  }

  function buildUrl(params) {
    const p = new URLSearchParams();
    if (params.category) p.set('category_name', params.category);
    if (params.tag) p.set('tag', params.tag);
    if (params.search) p.set('blog_search', params.search);
    if (params.page > 1) p.set('paged', String(params.page));
    const qs = p.toString();
    return qs ? baseUrl + (baseUrl.indexOf('?') !== -1 ? '&' : '?') + qs : baseUrl;
  }

  function setLoading(loading) {
    grid.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (loading) {
      grid.classList.add('opacity-60', 'pointer-events-none');
    } else {
      grid.classList.remove('opacity-60', 'pointer-events-none');
    }
  }

  function fetchAndUpdate(params) {
    setLoading(true);

    const url = new URL(ajaxUrl);
    url.searchParams.set('action', 'morrfithp_blog_archive');
    url.searchParams.set('nonce', nonce);
    if (params.category) url.searchParams.set('category', params.category);
    if (params.tag) url.searchParams.set('tag', params.tag);
    if (params.search) url.searchParams.set('blog_search', params.search);
    url.searchParams.set('page', String(params.page));

    fetch(url.toString())
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) {
          setLoading(false);
          return;
        }

        grid.innerHTML = data.posts_html || '';
        if (data.has_posts === false) {
          const noResults = document.createElement('div');
          noResults.id = 'blog-archive-no-results';
          noResults.className = 'col-span-full text-center py-12 border border-slate-800 bg-surface-dark';
          noResults.setAttribute('role', 'status');
          noResults.innerHTML = '<p class="text-slate-400 font-mono-tech uppercase tracking-widest">No posts match your search or filter.</p>';
          grid.appendChild(noResults);
        }

        if (featuredSection) {
          const wrap = featuredSection.querySelector('.blog-featured-wrap');
          if (wrap) {
            wrap.innerHTML = data.featured_html || '';
          }
          featuredSection.classList.toggle('hidden', !data.featured_html);
        }

        if (paginationContainer) {
          paginationContainer.innerHTML = data.pagination_html || '';
          bindPagination();
        }

        window.scrollTo({ top: grid.offsetTop - 100, behavior: 'smooth' });
      })
      .catch(() => {
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function updateUrlAndFetch(params, replace) {
    const url = buildUrl(params);
    if (replace) {
      window.history.replaceState({ blogParams: params }, '', url);
    } else {
      window.history.pushState({ blogParams: params }, '', url);
    }
    fetchAndUpdate(params);
  }

  function setActiveFilter(category, tag) {
    document.querySelectorAll(FILTER_BTN_SEL).forEach((btn) => {
      const btnCat = btn.getAttribute('data-blog-category') || '';
      const btnTag = btn.getAttribute('data-blog-tag') || '';
      const active = btnCat === (category || '') && btnTag === (tag || '');
      btn.setAttribute('data-active', active ? 'true' : 'false');
      btn.classList.toggle('bg-primary', active);
      btn.classList.toggle('text-background-dark', active);
      btn.classList.toggle('border-primary', active);
      btn.classList.remove('border-slate-700', 'text-slate-400', 'bg-background-dark');
      if (!active) {
        btn.classList.add('border-slate-700', 'text-slate-400', 'bg-background-dark');
      }
    });
  }

  function bindPagination() {
    const pageBtns = document.querySelectorAll('.js-blog-page');
    pageBtns.forEach((btn) => {
      btn.removeEventListener('click', handlePageClick);
      btn.addEventListener('click', handlePageClick);
    });
  }

  function handlePageClick(e) {
    const btn = e.currentTarget;
    const page = parseInt(btn.getAttribute('data-page'), 10);
    if (!page) return;
    const params = getUrlParams();
    params.page = page;
    updateUrlAndFetch(params);
  }

  function handleFilterClick(e) {
    e.preventDefault();
    const btn = e.currentTarget;
    const category = btn.getAttribute('data-blog-category') || '';
    const tag = btn.getAttribute('data-blog-tag') || '';
    const params = {
      category,
      tag,
      search: searchInput ? searchInput.value.trim() : '',
      page: 1,
    };
    setActiveFilter(category, tag);
    updateUrlAndFetch(params);
  }

  function handleSearch() {
    const query = searchInput ? searchInput.value.trim() : '';
    const params = getUrlParams();
    params.search = query;
    params.page = 1;
    setActiveFilter(params.category, params.tag);
    updateUrlAndFetch(params);
  }

  function initFromUrl() {
    const params = getUrlParams();
    setActiveFilter(params.category, params.tag);
    if (searchInput && params.search !== undefined) {
      searchInput.value = params.search;
    }
  }

  function init() {
    initFromUrl();

    document.querySelectorAll(FILTER_BTN_SEL).forEach((btn) => {
      btn.addEventListener('click', handleFilterClick);
    });

    if (searchInput) {
      searchInput.addEventListener('input', debounce(handleSearch, DEBOUNCE_MS));
      searchInput.closest('form')?.addEventListener('submit', (e) => {
        e.preventDefault();
        handleSearch();
      });
    }

    bindPagination();

    window.addEventListener('popstate', (e) => {
      const params = e.state?.blogParams || getUrlParams();
      setActiveFilter(params.category, params.tag);
      if (searchInput && params.search !== undefined) {
        searchInput.value = params.search;
      }
      fetchAndUpdate(params);
    });
  }

  init();
})();
